<template>
  <div class="q-my-md">
    <div class="text-h6 q-mb-sm">Define your Region</div>
    <market-autocomplete v-if="enableEdit" class="q-mb-md" @new-market="newMarket" />
    <q-card flat bordered>
      <google-map
        ref="mapRef"
        class="google-map"
        :api-promise="apiPromise"
        :api-key="apiKey"
        :styles="theme"
        :center="center"
        :zoom="zoom"
        :map-type-control-options="{
          position: 20
        }"
      >
        <custom-control v-if="enableEdit" position="TOP_LEFT">
          <q-btn-group class="bg-white" style="margin: 10px">
            <q-btn
              color="primary"
              flat
              icon="pan_tool"
              dense
              size="sm"
              class="q-pa-sm"
              :class="{ 'bg-grey-4': activeDrawingMode === 0 }"
              @click="setDrawingManager(0)"
            />
            <q-btn-dropdown
              color="primary"
              flat
              dense
              size="sm"
              :class="{ 'bg-grey-4': activeDrawingMode === 1 }"
              split
              icon="panorama_fish_eye"
              @click="setDrawingManager(1)"
            >
              <q-list>
                <q-item
                  v-for="miles in [1, 3, 5]"
                  :key="miles"
                  v-ripple
                  clickable
                  @click="createCircle(miles)"
                >
                  <q-item-section>{{ miles }}-Mile</q-item-section>
                </q-item>
              </q-list>
            </q-btn-dropdown>
            <q-btn
              color="primary"
              flat
              icon="polyline"
              dense
              size="sm"
              class="q-pa-sm"
              :class="{ 'bg-grey-4': activeDrawingMode === 2 }"
              @click="setDrawingManager(2)"
            />
            <q-btn
              color="primary"
              flat
              icon="crop_square"
              dense
              size="sm"
              class="q-pa-sm"
              :class="{ 'bg-grey-4': activeDrawingMode === 3 }"
              @click="setDrawingManager(3)"
            />
            <q-btn
              v-if="selectedOverlayEvent"
              icon="delete"
              text-color="red"
              dense
              size="sm"
              class="q-pa-sm"
              @click="deleteSelectedOverlay"
            />
          </q-btn-group>
          <q-spinner v-if="loading" color="primary" size="2em" />
        </custom-control>

        <google-marker
          v-for="prop in filteredProperties.filter((_) => _.units.length)"
          :key="prop.prop.id"
          :options="{
            optimized: true,
            position: { lat: prop.prop.lat!, lng: prop.prop.lon! },
            icon: '/marker-accent.svg'
          }"
          @click="openMarker"
        >
          <info-window ref="InfoWindowTest" :options="{ headerDisabled: true }" class="text-black">
            <div class="row items-center">
              <div v-if="!errorLoadingImage[prop.prop.id]" class="q-mr-md">
                <q-img
                  data-private
                  :src="propertyThumbnail(prop.prop)"
                  style="width: 152px"
                  alt="Building Image"
                  class="rounded-borders"
                  @error="errorLoadingImage[prop.prop.id] = true"
                />
              </div>
              <div>
                <div class="row q-col-gutter-md justify-between">
                  <div>
                    <div class="row items-center">
                      <div
                        class="q-mb-xs ellipsis"
                        style="font-size: 1.1rem; font-weight: 500; max-width: 300px"
                      >
                        {{ prop.prop.building_name || prop.prop.street_address }}
                      </div>
                      <new-property-report-icon :property-id="prop.prop.id" />
                    </div>
                    <div class="q-mb-sm text-grey-9 ellipsis" style="max-width: 300px">
                      {{ prop.prop.street_address }}, {{ prop.prop.city }}, {{ prop.prop.state }},
                      {{ prop.prop.zip_code }}
                    </div>
                  </div>
                  <div>
                    <!-- close the popup with a X icon, use bold for the icon -->
                    <q-icon
                      name="close"
                      class="cursor-pointer font-bold"
                      size="sm"
                      @click="closeInfoWindow"
                    />
                  </div>
                </div>
                <div class="row q-col-gutter-xs q-col-gutter-y-sm">
                  <div class="col-4">
                    <div class="metric-label"># Units</div>
                    <q-icon name="apartment" />
                    {{ prop.prop.number_units || '-' }}
                  </div>
                  <div class="col-4">
                    <div class="metric-label"># Stories</div>
                    <q-icon name="layers" />
                    {{ prop.prop.number_stories || '-' }}
                  </div>
                  <div class="col-4">
                    <div class="metric-label">Year Built</div>
                    <q-icon name="date_range" />
                    {{ prop.prop.year_built || '-' }}
                  </div>
                </div>
              </div>
            </div>
          </info-window>
        </google-marker>
      </google-map>
    </q-card>
  </div>
</template>
<script lang="ts">
import { watch, type PropType, toRaw } from 'vue'
import {
  type SimpleAvailability,
  type SimpleAvailabilityPeriod,
  type SimplePrice,
  type SimplePropertyDetailsResponse,
  type PropertySearchResponse,
  type MarketFilters,
  type MarketSearchResponse,
  getStreetViewUrl
} from 'common'
import { CustomControl, GoogleMap, Marker as GoogleMarker, InfoWindow } from 'vue3-google-map'
import apiPromise from '@/setups/googleMaps'
import { darkMapTheme, lightMapTheme, formatQuality } from '@/utils'

import MarketAutocomplete from './MarketAutocomplete.vue'
import NewPropertyReportIcon from '@/components/NewPropertyReportIcon.vue'

type AvailableOverlays = google.maps.Polygon | google.maps.Rectangle | google.maps.Circle

export default {
  name: 'MarketReportGoogleMap',
  components: {
    NewPropertyReportIcon,
    MarketAutocomplete,
    GoogleMap,
    GoogleMarker,
    CustomControl,
    InfoWindow
  },
  props: {
    filters: {
      type: Object as PropType<MarketFilters>,
      required: true
    },
    filteredProperties: {
      type: Array<{
        prop: SimplePropertyDetailsResponse
        units: {
          unit: SimpleAvailability
          lastHistory: SimplePrice
          period: SimpleAvailabilityPeriod
        }[]
      }>,
      required: true
    },
    enableEdit: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update-coordinates'],
  data() {
    return {
      activeDrawingMode: null as number | null,
      drawingManager: null as google.maps.drawing.DrawingManager | null,
      polyOptions: {
        strokeWeight: 2,
        strokeColor: '#10546A',
        fillColor: '#ECECEC',
        fillOpacity: 0.5,
        draggable: false
      },
      center: {
        lat: 41.8781,
        lng: -87.6298
      },
      zoom: 12,
      overlayEvents: [] as google.maps.drawing.OverlayCompleteEvent[],
      apiPromise,
      properties: [] as PropertySearchResponse[],
      apiKey: import.meta.env.VITE_GOOGLE_API_KEY,
      mapRef: null as null | typeof GoogleMap,
      selectedOverlayEvent: null as google.maps.drawing.OverlayCompleteEvent | null,
      errorLoadingImage: {} as Record<string, boolean>
    }
  },
  computed: {
    theme() {
      return this.$q.dark.isActive ? darkMapTheme : lightMapTheme
    }
  },
  created() {
    if (!this.filters.coordinates.length) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.center = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        }
      })
    }
  },
  mounted() {
    this.mapRef = this.$refs['mapRef'] as typeof GoogleMap
    watch(() => this.mapRef?.ready, this.onLoad)
  },
  methods: {
    formatQuality,
    propertyThumbnail(prop: SimplePropertyDetailsResponse) {
      return getStreetViewUrl(prop, import.meta.env.VITE_API_URL)
    },
    closeInfoWindow() {
      const infoWindows = this.$refs['InfoWindowTest'] as (typeof InfoWindow)[]
      for (const infoWindow of infoWindows) {
        infoWindow.close()
      }
    },
    openMarker() {
      this.closeInfoWindow()
    },
    coordinatesFromRectangle(overlay: google.maps.Rectangle) {
      const bounds = overlay.getBounds()!
      const NECorner = bounds.getNorthEast()
      const SWCorner = bounds.getSouthWest()
      return [
        [SWCorner.lng(), NECorner.lat()],
        [NECorner.lng(), NECorner.lat()],
        [NECorner.lng(), SWCorner.lat()],
        [SWCorner.lng(), SWCorner.lat()],
        [SWCorner.lng(), NECorner.lat()]
      ]
    },
    coordinatesFromPolygon(overlay: google.maps.Polygon) {
      const paths = overlay.getPaths()
      const points: [number, number][] = []
      for (const path of paths.getArray()) {
        for (const latLng of path.getArray()) {
          points.push([latLng.lng(), latLng.lat()])
        }
      }
      points.push(points[0])
      return points
    },
    coordinatesFromCircle(overlay: google.maps.Circle) {
      const center = overlay.getCenter()!
      const radius = overlay.getRadius()
      const points: [number, number][] = []
      const earthRadius = 6371000
      for (let i = 0; i < 360; i++) {
        const bearing = (i * Math.PI) / 180
        const lat1 = center.lat() * (Math.PI / 180)
        const lon1 = center.lng() * (Math.PI / 180)
        const lat2 = Math.asin(
          Math.sin(lat1) * Math.cos(radius / earthRadius) +
            Math.cos(lat1) * Math.sin(radius / earthRadius) * Math.cos(bearing)
        )
        const lon2 =
          lon1 +
          Math.atan2(
            Math.sin(bearing) * Math.sin(radius / earthRadius) * Math.cos(lat1),
            Math.cos(radius / earthRadius) - Math.sin(lat1) * Math.sin(lat2)
          )
        const latitude = lat2 * (180 / Math.PI)
        const longitude = lon2 * (180 / Math.PI)
        points.push([longitude, latitude])
      }
      points.push(points[0])
      return points
    },
    recomputeCoordinates() {
      const overlayCoordinates = this.overlayEvents.map((event) => {
        if (event.type === google.maps.drawing.OverlayType.RECTANGLE) {
          return [this.coordinatesFromRectangle(event.overlay as google.maps.Rectangle)]
        } else if (event.type === google.maps.drawing.OverlayType.POLYGON) {
          return [this.coordinatesFromPolygon(event.overlay as google.maps.Polygon)]
        } else if (event.type === google.maps.drawing.OverlayType.CIRCLE) {
          return [this.coordinatesFromCircle(event.overlay as google.maps.Circle)]
        }
        return []
      })
      this.$emit('update-coordinates', overlayCoordinates)
    },
    isCoordinatesRectangle(coords: number[][]) {
      if (coords.length !== 5) return false
      const dist = (c1: number[], c2: number[]) => (c1[0] - c2[0]) ** 2 + (c1[1] - c2[1]) ** 2
      return (
        dist(coords[0], coords[1]) === dist(coords[2], coords[3]) &&
        dist(coords[1], coords[2]) === dist(coords[3], coords[4])
      )
    },
    onLoad() {
      if (this.enableEdit) {
        this.drawingManager = new google.maps.drawing.DrawingManager({
          drawingControlOptions: {
            drawingModes: [
              google.maps.drawing.OverlayType.CIRCLE,
              google.maps.drawing.OverlayType.POLYGON,
              google.maps.drawing.OverlayType.RECTANGLE
            ]
          },
          drawingControl: false,
          rectangleOptions: this.polyOptions,
          circleOptions: this.polyOptions,
          polygonOptions: this.polyOptions,
          map: this.mapRef!.map
        })
        google.maps.event.addListener(
          this.drawingManager,
          'overlaycomplete',
          (newEvent: google.maps.drawing.OverlayCompleteEvent) => this.onOverlayComplete(newEvent)
        )
        google.maps.event.addListener(this.drawingManager, 'drawingmode_changed', () =>
          this.clearSelection()
        )
        google.maps.event.addListener(this.mapRef!.map, 'click', () => this.clearSelection())
      }
      this.filters.coordinates.forEach((coor) => {
        let type = null as null | google.maps.drawing.OverlayType
        let overlay = null as null | AvailableOverlays
        if (this.isCoordinatesRectangle(coor[0])) {
          type = google.maps.drawing.OverlayType.RECTANGLE
          overlay = new google.maps.Rectangle({
            ...this.polyOptions,
            map: this.mapRef!.map,
            bounds: new google.maps.LatLngBounds(
              new google.maps.LatLng(coor[0][0][1], coor[0][0][0]),
              new google.maps.LatLng(coor[0][2][1], coor[0][2][0])
            )
          })
          this.setRectangleListeners(overlay)
        } else if (coor[0].length === 361) {
          // No way someone creates a 361 point polygon that is not a circle
          const center = new google.maps.LatLng(
            (coor[0][0][1] + coor[0][179][1]) / 2,
            (coor[0][0][0] + coor[0][179][0]) / 2
          )
          type = google.maps.drawing.OverlayType.CIRCLE
          overlay = new google.maps.Circle({
            ...this.polyOptions,
            map: this.mapRef!.map,
            center: center,
            radius: google.maps.geometry.spherical.computeDistanceBetween(
              center,
              new google.maps.LatLng(coor[0][179][1], coor[0][179][0])
            )
          })
          this.setCircleListeners(overlay)
        } else {
          type = google.maps.drawing.OverlayType.POLYGON
          overlay = new google.maps.Polygon({
            ...this.polyOptions,
            map: this.mapRef!.map,
            paths: coor[0].map((point) => ({
              lat: point[1],
              lng: point[0]
            }))
          })
          this.setPolygonListeners(overlay)
        }
        if (this.enableEdit) {
          overlay.addListener('click', () => {
            this.clearSelection()
            overlay.setEditable(true)
            overlay.setDraggable(true)
            this.selectedOverlayEvent = { type, overlay }
          })
        }
        this.overlayEvents.push({ type, overlay })
      })
      this.centerMapOnOverlays()
    },
    centerMapOnOverlays() {
      if (this.filters.coordinates.length) {
        const bounds = new google.maps.LatLngBounds()
        this.filters.coordinates.forEach((c) => {
          c[0].forEach((coord) => bounds.extend({ lat: coord[1], lng: coord[0] }))
        })
        this.mapRef!.map.fitBounds(bounds)
      }
    },
    onOverlayComplete(newEvent: google.maps.drawing.OverlayCompleteEvent) {
      const newOverlay = newEvent.overlay as AvailableOverlays
      if (this.drawingManager) {
        this.drawingManager.setDrawingMode(null)
      }
      this.overlayEvents.push(newEvent)
      if (newOverlay) {
        newOverlay.addListener('click', () => {
          this.clearSelection()
          newOverlay.setEditable(true)
          newOverlay.setDraggable(true)
          this.selectedOverlayEvent = newEvent
        })
      }
      if (newEvent.type === google.maps.drawing.OverlayType.POLYGON) {
        this.setPolygonListeners(newOverlay as google.maps.Polygon)
      } else if (newEvent.type === google.maps.drawing.OverlayType.RECTANGLE) {
        this.setRectangleListeners(newOverlay as google.maps.Rectangle)
      } else if (newEvent.type === google.maps.drawing.OverlayType.CIRCLE) {
        this.setCircleListeners(newOverlay as google.maps.Circle)
      }
      this.clearSelection()
      newOverlay.setEditable(true)
      newOverlay.setDraggable(true)
      this.selectedOverlayEvent = newEvent
      this.activeDrawingMode = null
      this.recomputeCoordinates()
    },
    setPolygonListeners(newOverlay: google.maps.Polygon) {
      newOverlay.addListener('dragstart', () => {
        newOverlay.getPaths().forEach((path) => {
          google.maps.event.clearListeners(path, 'set_at')
        })
      })
      newOverlay.addListener('dragend', () => {
        newOverlay.getPaths().forEach((path) => {
          google.maps.event.addListener(path, 'set_at', () => {
            this.recomputeCoordinates()
          })
        })
        this.recomputeCoordinates()
      })
      newOverlay.getPaths().forEach((path) => {
        google.maps.event.addListener(path, 'insert_at', () => {
          this.recomputeCoordinates()
        })
        google.maps.event.addListener(path, 'set_at', () => {
          this.recomputeCoordinates()
        })
        google.maps.event.addListener(path, 'remove_at', () => {
          this.recomputeCoordinates()
        })
      })
    },
    setRectangleListeners(newOverlay: google.maps.Rectangle) {
      newOverlay.addListener('dragstart', () => {
        google.maps.event.clearListeners(newOverlay, 'bounds_changed')
      })
      newOverlay.addListener('dragend', () => {
        newOverlay.addListener('bounds_changed', () => {
          this.recomputeCoordinates()
        })
        this.recomputeCoordinates()
      })
      newOverlay.addListener('bounds_changed', () => {
        this.recomputeCoordinates()
      })
    },
    setCircleListeners(newOverlay: google.maps.Circle) {
      newOverlay.addListener('dragstart', () => {
        google.maps.event.clearListeners(newOverlay, 'center_changed')
      })
      newOverlay.addListener('dragend', () => {
        newOverlay.addListener('center_changed', () => {
          this.recomputeCoordinates()
        })
        this.recomputeCoordinates()
      })
      newOverlay.addListener('center_changed', () => {
        this.recomputeCoordinates()
      })
      newOverlay.addListener('radius_changed', () => {
        this.recomputeCoordinates()
      })
    },
    clearSelection() {
      if (this.selectedOverlayEvent) {
        const overlay = this.selectedOverlayEvent.overlay as AvailableOverlays
        overlay.setEditable(false)
        overlay.setDraggable(false)
        this.selectedOverlayEvent = null
      }
    },
    deleteSelectedOverlay() {
      if (this.selectedOverlayEvent) {
        const overlayIndex = this.overlayEvents.findIndex(
          (_) => _.overlay === this.selectedOverlayEvent!.overlay
        )
        if (overlayIndex >= 0) {
          this.overlayEvents = this.overlayEvents
            .slice(0, overlayIndex)
            .concat(this.overlayEvents.slice(overlayIndex + 1))
          toRaw(this.selectedOverlayEvent.overlay!).setMap(null)
          this.selectedOverlayEvent = null
          this.recomputeCoordinates()
        }
      }
    },
    setDrawingManager(state: number) {
      if (this.drawingManager) {
        this.activeDrawingMode = state > 0 ? state : null
        switch (state) {
          case 0:
            this.clearSelection()
            return this.drawingManager.setDrawingMode(null)
          case 1:
            return this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.CIRCLE)
          case 2:
            return this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON)
          case 3:
            return this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.RECTANGLE)
        }
      }
    },
    createCircle(miles: number) {
      if (this.mapRef) {
        const circle = new google.maps.Circle({
          ...this.polyOptions,
          map: this.mapRef.map,
          center: this.mapRef.map.getCenter(),
          radius: miles * 1609.34
        })
        this.onOverlayComplete({
          type: google.maps.drawing.OverlayType.CIRCLE,
          overlay: circle
        })
      }
    },
    newMarket(market: MarketSearchResponse) {
      const type = google.maps.drawing.OverlayType.POLYGON
      const overlay = new google.maps.Polygon({
        ...this.polyOptions,
        map: this.mapRef!.map,
        paths: market.coordinates.map((point) => ({
          lat: point[1],
          lng: point[0]
        }))
      })
      this.onOverlayComplete({ type, overlay })
      overlay.setEditable(false)
      this.selectedOverlayEvent = null
      this.$nextTick(() => {
        this.centerMapOnOverlays()
      })
    }
  }
}
</script>
<style scoped>
.google-map {
  width: 100%;
  min-height: 300px;
  height: calc(100vh / 4);
}
</style>
